"use client";

import { CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";

import { kaizenTheme } from "./kaizen-theme";

import type { Theme } from "@mui/material/styles";
import type { ReactNode } from "react";

export interface KaizenThemeProviderProps {
  /**
   * The theme to mount. Defaults to `kaizenTheme`. An app that layers its own
   * options onto `kaizenThemeOptions` passes the composed result so the
   * provider still owns the mode default and the baseline.
   */
  theme?: Theme;
  children: ReactNode;
}

/**
 * Mounts the Kaizen theme the way every app should: light by default, dark
 * only when the reader opts in through the scheme toggle, with MUI's baseline
 * styles applied.
 *
 * `defaultMode="light"` matters because the theme declares a dark scheme and
 * MUI otherwise follows the OS, so a reader in OS dark mode would land in dark
 * without choosing it.
 *
 * A server-rendered app renders MUI's `InitColorSchemeScript` with
 * `defaultMode="light"` as the first child of the body so the stored mode
 * applies before hydration. A Next.js app supplies its own MUI cache provider.
 */
export const KaizenThemeProvider = ({
  theme = kaizenTheme,
  children,
}: KaizenThemeProviderProps): ReactNode => (
  <ThemeProvider theme={theme} defaultMode="light">
    <CssBaseline />
    {children}
  </ThemeProvider>
);
